import { useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { subscribe, sendContact } from "../lib/api";
import { getRecaptchaToken } from "../lib/recaptcha";

const emptyContact = (interest = "Tickets") => ({ name: "", email: "", interest, message: "" });

function ContactForm({ section, form, setForm, interests, lockedToTickets = false, loading, onSubmit, testIdPrefix = "contact" }) {
  const visibleInterests = lockedToTickets ? ["Tickets"] : interests;

  return (
    <form onSubmit={onSubmit} data-testid={`${testIdPrefix}-form`} className="flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <input
          data-testid={`${testIdPrefix}-name-input`}
          type="text"
          placeholder={section.metadata?.name_placeholder || "Name"}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="field-underline font-dm text-[#FAFAFA] placeholder:text-[#A3A3A3]/60 py-3"
        />
        <input
          data-testid={`${testIdPrefix}-email-input`}
          type="email"
          placeholder={section.metadata?.contact_email_placeholder || "Email"}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="field-underline font-dm text-[#FAFAFA] placeholder:text-[#A3A3A3]/60 py-3"
        />
      </div>

      <div className="flex flex-wrap gap-3">
        {visibleInterests.map((option) => (
          <button
            key={option}
            type="button"
            data-testid={`${testIdPrefix}-interest-${option.toLowerCase().replace(/[^a-z]/g, "")}`}
            onClick={() => !lockedToTickets && setForm({ ...form, interest: option })}
            disabled={lockedToTickets}
            className={`font-oswald uppercase tracking-[0.15em] text-xs px-4 py-2 border transition-colors duration-300 ${
              form.interest === option
                ? "border-[#E85A24] bg-[#E85A24] text-black"
                : "border-white/20 text-[#A3A3A3] hover:border-[#E85A24] hover:text-[#E85A24]"
            } ${lockedToTickets ? "cursor-default" : ""}`}
          >
            {option}
          </button>
        ))}
      </div>

      <textarea
        data-testid={`${testIdPrefix}-message-input`}
        rows={5}
        placeholder={section.metadata?.message_placeholder || "Your message"}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        className={lockedToTickets
          ? "font-dm bg-white text-black placeholder:text-black/50 py-3 px-3 resize-none border border-white outline-none focus:border-[#E85A24]"
          : "field-underline font-dm text-[#FAFAFA] placeholder:text-[#A3A3A3]/60 py-3 resize-none"}
      />

      <button
        data-testid={`${testIdPrefix}-submit`}
        type="submit"
        disabled={loading}
        className="self-start font-oswald uppercase tracking-[0.2em] text-sm border border-[#E85A24] text-[#E85A24] px-8 py-4 hover:bg-[#E85A24] hover:text-black transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? "Sending…" : section.metadata?.contact_button_label || "Send Message"}
      </button>
    </form>
  );
}

export const Join = ({ section }) => {
  const [email, setEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [form, setForm] = useState(emptyContact());
  const [msgLoading, setMsgLoading] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState(emptyContact());
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const interests = useMemo(() => section?.metadata?.interests || [], [section?.metadata?.interests]);

  useEffect(() => {
    if (interests.length > 0 && !interests.includes(form.interest)) {
      setForm((current) => ({ ...current, interest: interests[0] }));
    }
  }, [interests, form.interest]);

  useEffect(() => {
    const handleEventEnquiry = (event) => {
      const eventTitle = event?.detail?.eventTitle;
      if (!eventTitle) return;

      setEnquiryForm({
        name: "",
        email: "",
        interest: "Tickets",
        message: `Enquiring about ${eventTitle}.\n\nEnter your message here.`,
      });
      setEnquiryOpen(true);
    };

    window.addEventListener("susegado:event-enquiry", handleEventEnquiry);
    return () => window.removeEventListener("susegado:event-enquiry", handleEventEnquiry);
  }, []);

  if (!section) return null;

  const messageError = (err, fallback) => err?.response?.data?.detail || fallback;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubLoading(true);
    try {
      const captchaToken = await getRecaptchaToken("newsletter_subscribe");
      await subscribe(email.trim(), captchaToken);
      toast.success("You're on the list. Welcome to the strike.", { duration: 5000 });
      setEmail("");
    } catch (err) {
      toast.error(messageError(err, "Something went wrong. Try again."));
    } finally {
      setSubLoading(false);
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }

    setMsgLoading(true);
    try {
      const captchaToken = await getRecaptchaToken("contact_form");
      await sendContact(form, captchaToken, "contact_form");
      setForm(emptyContact(interests[0] || "Tickets"));
      setSuccessOpen(true);
    } catch (err) {
      toast.error(messageError(err, "Could not send message. Try again."));
    } finally {
      setMsgLoading(false);
    }
  };

  const handleEnquiry = async (e) => {
    e.preventDefault();
    if (!enquiryForm.name.trim() || !enquiryForm.email.trim() || !enquiryForm.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }

    setEnquiryLoading(true);
    try {
      const captchaToken = await getRecaptchaToken("event_enquiry");
      await sendContact(enquiryForm, captchaToken, "event_enquiry");
      setEnquiryOpen(false);
      setEnquiryForm(emptyContact());
      setSuccessOpen(true);
    } catch (err) {
      toast.error(messageError(err, "Could not send enquiry. Try again."));
    } finally {
      setEnquiryLoading(false);
    }
  };

  return (
    <>
      <section id="join" data-testid="join-section" className="relative py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <Reveal>
              <SectionLabel index={section.metadata?.section_index || "04"}>{section.eyebrow}</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-anton uppercase text-[#FAFAFA] text-4xl md:text-6xl leading-[0.9] mb-6">
                {section.title} <span className="text-[#E85A24]">{section.title_accent}</span>
              </h2>
            </Reveal>
            {section.body && (
              <Reveal delay={0.15}>
                <p className="font-dm text-[#A3A3A3] mb-10 max-w-md">{section.body}</p>
              </Reveal>
            )}
            <Reveal delay={0.2}>
              <form onSubmit={handleSubscribe} data-testid="newsletter-form" className="flex flex-col sm:flex-row gap-4 max-w-lg">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={section.metadata?.email_placeholder || "your@email.com"}
                  className="field-underline flex-1 font-dm text-lg text-[#FAFAFA] placeholder:text-[#A3A3A3]/60 py-3"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={subLoading}
                  className="font-oswald uppercase tracking-[0.2em] text-sm bg-[#E85A24] text-black px-8 py-4 hover:bg-[#FF7038] transition-colors duration-300 disabled:opacity-60"
                >
                  {subLoading ? "Joining…" : section.button_label || "Subscribe"}
                </button>
              </form>
            </Reveal>
          </div>

          <div className="border border-white/10 p-8 md:p-10 bg-[#050505]">
            <Reveal>
              <h3 className="font-anton uppercase text-2xl md:text-3xl text-[#FAFAFA] mb-2">
                {section.metadata?.contact_title || "Get in"}{" "}
                <span className="text-[#E85A24]">{section.metadata?.contact_title_accent || "touch"}</span>
              </h3>
              <p className="font-dm text-sm text-[#A3A3A3] mb-8">
                {section.metadata?.contact_body || "Tickets, sponsorship, tryouts or press — tell us what you need."}
              </p>
            </Reveal>
            <ContactForm
              section={section}
              form={form}
              setForm={setForm}
              interests={interests}
              loading={msgLoading}
              onSubmit={handleContact}
            />
          </div>
        </div>
      </section>

      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="bg-[#050505] border border-white/10 text-[#FAFAFA] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto p-8 md:p-10">
          <DialogHeader className="text-left mb-2">
            <p className="font-oswald uppercase tracking-[0.3em] text-xs text-[#E85A24]">Tickets</p>
            <DialogTitle className="font-anton uppercase !tracking-[0.04em] text-3xl md:text-4xl text-[#FAFAFA]">
              Event <span className="text-[#E85A24]">Enquiry</span>
            </DialogTitle>
          </DialogHeader>
          <ContactForm
            section={section}
            form={enquiryForm}
            setForm={setEnquiryForm}
            interests={["Tickets"]}
            lockedToTickets
            loading={enquiryLoading}
            onSubmit={handleEnquiry}
            testIdPrefix="enquiry"
          />
        </DialogContent>
      </Dialog>

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="bg-[#050505] border-2 border-[#E85A24] text-[#FAFAFA] rounded-none max-w-md p-8 md:p-10 text-center">
          <div className="flex flex-col items-center">
            <CheckCircle2 size={56} className="text-[#E85A24] mb-5" />
            <DialogHeader className="items-center">
              <DialogTitle className="font-anton uppercase text-3xl text-[#FAFAFA]">Message Sent</DialogTitle>
            </DialogHeader>
            <p className="font-dm text-[#C4C4C4] mt-3 mb-7">
              Thank you. Your message has been received and our team will reach out shortly.
            </p>
            <button
              type="button"
              onClick={() => setSuccessOpen(false)}
              className="font-oswald uppercase tracking-[0.2em] text-sm bg-[#E85A24] text-black px-8 py-3 hover:bg-[#FF7038] transition-colors duration-300"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
